// Helpers to translate ProjectWizard state into the existing backend API payloads
// and run a per-room selection against the right endpoint.

import axios from "axios";
import { computeRoomUnitPlan, lengthCapacityList } from "./unitSchedule";
import { paSensibleCapacity, paLatentCapacity } from "./psychro";
import { formatApiError } from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Coil mapping (subset — reused from SelectionTool to know which endpoint)
const MODEL_N_LIKE = ["Model N", "Model N-f"];

// Model D shares Model C coil data but is a smaller physical unit; every
// capacity value is scaled down by this factor relative to Model C.
// Must match backend server.py MODEL_D_CAPACITY_DERATE = 1 / 1.21.
export const MODEL_D_CAPACITY_DERATE = 1.0 / 1.21;

const PRIMARY_AIR_LIKE = [
  "Model C",
  "Model C1",
  "Model D",
  "Model H",
  "Model I-L",
  "Model I-T",
  "Model I-F",
  "Model L-L",
  "Model L-T",
  "Model L-F",
];
const O_DUAL = ["Model O-2"];
const SINGLE_ACB = ["Model S", "Model S-HW", "Model O-1"];

export const ALL_MODELS = [
  ...SINGLE_ACB,
  ...PRIMARY_AIR_LIKE,
  ...O_DUAL,
  ...MODEL_N_LIKE,
];

export function isModelN(model) {
  return MODEL_N_LIKE.includes(model);
}
export function isModelO2(model) {
  return O_DUAL.includes(model);
}
export function isPrimaryAir(model) {
  return PRIMARY_AIR_LIKE.includes(model);
}

// ---------------------------------------------------------------------------
// pickCascadeOperatingPoint — canonical cascade-speed resolver.
//
// rawResult: one entry from result.raw.results[N] (a single length entry)
//            from the multi-length endpoint.
// project:   full project object (for advanced.acb_fan_speed fallback).
//
// Returns:
//   {
//     speed,            // "low" | "med" | "high" | "pa" (string, lower-case)
//     speedRow,         // the speed entry object from rawResult.cooling.speeds
//     opt,              // rawResult.optimal_point || {}
//     waterFlowGpm,     // modulated water flow (opt.water_flow_gpm preferred)
//     waterModulated,   // true when optimal_point provided a flow override
//     basis,            // rawResult.capacity_basis or "water_side"
//     strategyUsed,     // opt.acb_strategy_used (ACB only)
//     strategyMinimised,// opt.strategy_minimised (ACB only)
//     strategyNote,     // opt.strategy_note (ACB only)
//   }
//
// Precedence for speed selection:
//   1. optimal_point.operating_speed  — actual engine cascade choice
//   2. project.advanced.acb_fan_speed — user-selected speed (survives max sweep)
//   3. "med"                          — AHRI 1335 / backend default
//   4. speeds[0]                      — last resort
// ---------------------------------------------------------------------------
export function pickCascadeOperatingPoint(rawResult, project) {
  const speeds = rawResult?.cooling?.speeds || [];
  const opt = rawResult?.optimal_point || {};

  const _speedKey = (opt.operating_speed
    || (project?.advanced?.acb_fan_speed || "").toLowerCase()
    || null);

  const speedRow = (_speedKey && speeds.find((s) => s.speed === _speedKey))
    || speeds.find((s) => s.speed === "med")
    || speeds[speeds.length - 1]
    || {};

  const speed = speedRow.speed || _speedKey || "med";

  const waterFlowGpm = opt.water_flow_gpm
    ?? speedRow.water_flow_gpm
    ?? speedRow.water_side?.water_flow_gpm
    ?? rawResult?.water_flow_gpm
    ?? null;

  const waterModulated = opt.water_flow_gpm != null;

  // AHRI 1335 single-basis lock: basis is always room_load.
  // rawResult.capacity_basis is echoed from backend (will be "room_load" for
  // all new requests). Fallback kept for cached/stale responses.
  const basis = rawResult?.capacity_basis || opt.target_btuh_basis || "room_load";

  return {
    speed,
    speedRow,
    opt,
    waterFlowGpm,
    waterModulated,
    basis,
    strategyUsed: opt.acb_strategy_used ?? null,
    strategyMinimised: opt.strategy_minimised ?? null,
    strategyNote: opt.strategy_note ?? null,
  };
}

// ---- Family-detect helpers (B.1.5) ----
// FACB = Fan-Assisted Active Chilled Beam: Model S, S-HW, O-1, O-2
export function isFACB(model) {
  return SINGLE_ACB.includes(model) || O_DUAL.includes(model);
}
// ACB = Active Chilled Beam / Induction (no fan): Model C, C1, H, I-*, L-*
export function isACB(model) {
  return PRIMARY_AIR_LIKE.includes(model);
}
// EWS = ERV-based system: Model N, N-f
export function isEWS(model) {
  return MODEL_N_LIKE.includes(model);
}

// isCBFamily: true for every chilled-beam variant (ACB + FACB). Excludes EWS.
export function isCBFamily(model) {
  return isACB(model) || isFACB(model);
}

// Returns { hasFACB, hasACB, hasEWS } for a rooms array.
// Pass an empty array (or null) when rooms are not yet known — all false.
export function getProjectFamilies(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const models = list.map((r) => r?.unit_model).filter(Boolean);
  return {
    hasFACB: models.some(isFACB),
    hasACB: models.some(isACB),
    hasEWS: models.some(isEWS),
  };
}

function modelToCoilTypes(model) {
  if (isModelN(model)) return ["PAC", "Spinnaker"];
  if (model === "Model C" || model === "Model D") return ["Model C"];
  if (model === "Model C1") return ["Model C1"];
  if (isModelO2(model)) return ["Model Op", "ACB"];
  return ["ACB"];
}

// ---- Build payload for /api/calculate-multi-length (S, S-HW, O-1, O-2, PA models) ----
export function buildMultiLengthPayload(project, room, opts = {}) {
  const { omitTarget = false } = opts;
  const dc = project.designConditions;
  const advanced = project.advanced || {};
  const model = room.unit_model;

  const payload = {
    coil_types: modelToCoilTypes(model),
    unit_type: model,
    outdoor_db_f: parseFloat(dc.outdoor_db_f),
    outdoor_wb_f:
      parseFloat(dc.outdoor_wb_f) || parseFloat(dc.outdoor_db_f) - 5,
    outdoor_db_winter_f: parseFloat(dc.outdoor_db_winter_f) || 10,
    // Field names MUST match the backend `MultiLengthCalculateRequest`
    // schema. Send all 4 aliases (room_db_f / room_summer_dbt_f etc.) so
    // both the new and legacy endpoints pick up the correct values rather
    // than silently falling back to Pydantic defaults (75°F / 50% RH).
    room_db_f: parseFloat(dc.room_db_f),
    room_summer_dbt_f: parseFloat(dc.room_db_f),
    room_rh: parseFloat(dc.room_rh),
    room_rh_pct: parseFloat(dc.room_rh),
    room_db_winter_f: parseFloat(dc.room_db_winter_f) || 70,
    room_winter_dbt_f: parseFloat(dc.room_db_winter_f) || 70,
    room_rh_winter: parseFloat(dc.room_rh_winter) || 30,
    room_winter_rh_pct: parseFloat(dc.room_rh_winter) || 30,
    water_entering_f: parseFloat(dc.water_entering_f) || 45,
    water_entering_summer_f: parseFloat(dc.water_entering_f) || 45,
    water_entering_winter_f: parseFloat(dc.water_entering_winter_f) || 120,
    altitude_ft: parseFloat(dc.altitude_ft) || 0,
    oa_flow_cfm: parseFloat(room.oa_flow_cfm) || 0,
    // No length_ft — backend iterates all lengths and we pick the shortest
    // that meets the target capacity in extractMultiLengthSummary.
  };

  // Air-On override (project-level)
  if (advanced.air_on_temp_f) {
    payload.air_on_temp_f = parseFloat(advanced.air_on_temp_f);
  }
  if (advanced.air_on_temp_heating_f) {
    payload.air_on_temp_heating_f = parseFloat(advanced.air_on_temp_heating_f);
  }

  // Target capacity (room-level) — backend field is `target_cooling_btuh`;
  // when set, the backend modulates water flow to match this exactly AND
  // filters lengths with delivered_total < 95% of target.
  // When the user only supplies SENSIBLE (ACB / IU sensible-load products),
  // we send `target_sensible_capacity_btuh` instead of `target_cooling_btuh`
  // so the backend doesn't incorrectly drop lengths whose TOTAL is below
  // the sensible target (a 3 ft unit may deliver 4220 W total but 4970 W
  // sensible — both numbers describe the same coil at the same operating
  // point, just measured differently).
  const totalTarget = parseFloat(room.cooling_total_btuh);
  const sensTarget = parseFloat(room.cooling_sensible_btuh);
  const hasTotal = !isNaN(totalTarget) && totalTarget > 0;
  const hasSens = !isNaN(sensTarget) && sensTarget > 0;
  // omitTarget=true: skip cooling targets so backend runs at MAX water flow
  // and returns true max capacity per length (used for initial pass first
  // probe per the engineer-facing workflow: 1. PA + Max water + Max length
  // → Max capacity, then decide whether to modulate down or bump nozzles).
  if (!omitTarget) {
    if (hasTotal) {
      payload.target_cooling_btuh = totalTarget;
      if (hasSens) payload.target_sensible_capacity_btuh = sensTarget;
    } else if (hasSens) {
      // Sensible-only (ACB / IU): mirror onto BOTH fields so the backend
      // modulates water flow + triggers Stage A2 sensible-target modulation.
      payload.target_cooling_btuh = sensTarget;
      payload.target_sensible_capacity_btuh = sensTarget;
    }
  }
  if (room.heating_btuh) {
    payload.target_heating_btuh = parseFloat(room.heating_btuh);
  }

  // Target water flow (project-level optional)
  if (advanced.water_flow_gpm) {
    payload.water_flow_gpm = parseFloat(advanced.water_flow_gpm);
  }

  // Glycol (project-level)
  if (advanced.use_glycol && advanced.glycol_percentage) {
    payload.glycol_type = advanced.glycol_type || "PG";
    payload.glycol_pct = parseFloat(advanced.glycol_percentage);
  }

  // Model O-2: serial/parallel — backend field name is `piping`.
  if (isModelO2(model) && advanced.o2_circuit) {
    payload.piping = advanced.o2_circuit; // "serial" | "parallel"
  }

  // Primary-Air models (Model I-*, L-*, C, C1, H): the Room Schedule's OA
  // field is REPURPOSED as the Primary Air (PA) flow for these models.
  // PA EAT (DB + RH) come from the project's Design Conditions tab. If left
  // blank, the backend defaults to outdoor design conditions.
  if (isPrimaryAir(model)) {
    payload.pa_eat_f = parseFloat(dc.pa_eat_db_f) || null;
    payload.pa_rh_pct = parseFloat(dc.pa_eat_rh) || null;
    payload.pa_cfm = parseFloat(room.oa_flow_cfm) || null;
    payload.pa_pressure_inwg = parseFloat(advanced.pa_pressure_in_wg) || null;
    // Winter PA inlet conditions (Part B, 2026-05-24). Optional — backend falls
    // back to summer values when these are null (backwards-compatible).
    const winterDb = parseFloat(dc.pa_eat_db_winter_f);
    const winterRh = parseFloat(dc.pa_eat_rh_winter);
    payload.pa_eat_winter_f = isNaN(winterDb) ? null : winterDb;
    payload.pa_eat_winter_rh = isNaN(winterRh) ? null : winterRh;
    // B.1.6 — per-room PA static pressure (Summary tab editable column).
    // TODO B.2: wire backend-side to consume pa_static_pressure_inwg per-room.
    // For now this field is forwarded non-fatally; pydantic strict mode may
    // silently ignore it if extra fields are allowed (default FastAPI behaviour).
    const paSP = parseFloat(room.pa_static_pressure_inwg);
    if (!isNaN(paSP) && paSP > 0) {
      payload.pa_static_pressure_inwg = paSP;
    }
    // ACB spigot / air-inlet bore (Phase 3 NC). Per-room override wins; falls
    // back to the project-level default; absent → backend defaults to 6". The
    // value drives NC only for C/C1/D (spigot-dependent); for flow-only ACBs
    // the backend ignores it. Stored on the room as `air_inlet_size` by the
    // Configurator and the Summary per-row dropdown.
    const rawBore = room.air_inlet_size != null
      ? room.air_inlet_size
      : advanced.default_acb_spigot_in;
    const bore = parseInt(rawBore, 10);
    if (Number.isFinite(bore) && [4, 6, 8, 10].includes(bore)) {
      payload.acb_spigot_bore_in = bore;
    }
  }

  // ACB fan speed (Model S, S-HW, O-1, O-2): hold the selected speed;
  // engine rates capacity at that speed and escalates length if needed.
  payload.acb_fan_speed = String(advanced?.acb_fan_speed || "MED").toUpperCase();

  // B.1.5 — capacity_basis, facb_strategy, acb_strategy: project-level fields
  // that round-trip to the backend unchanged. Defaults match backend Pydantic
  // defaults so legacy projects that pre-date B.1 keep producing identical
  // selections. `selection_strategy` (the old B.1 name) is no longer sent.
  //
  // AHRI 1335 single-basis lock (see commit message): the UI selector was removed.
  // Backend still accepts capacity_basis for direct callers / tests.
  // Legacy projects with saved capacity_basis="water_side" are silently coerced here.
  payload.capacity_basis = "room_load";
  payload.facb_strategy = project.facb_strategy || "lowest_speed";
  payload.acb_strategy = project.acb_strategy || "given_flow_given_sp";

  // B.6 — Water-side limits: max DP cap + water flow cap (max_gpm / gpm_per_ton).
  // Fields live directly on `project` (same level as capacity_basis / facb_strategy).
  // max_water_dp: null → backend applies its default of 20 ftH2O.
  const maxDp = parseFloat(project.max_water_dp);
  payload.max_water_dp = Number.isFinite(maxDp) && maxDp > 0 ? maxDp : null;

  // Coil pipe count — drives the 4-pipe capacity derate on the backend
  // (cooling × 0.847, heating × 0.8) for all FACB / ACB / IU models.
  // Set on the Selection Criteria tab's Water section; default 2.
  const pipes = parseInt(project?.advanced?.no_coil_pipes, 10);
  payload.no_coil_pipes = (pipes === 4) ? 4 : 2;

  // ACB circuit-count override — engineer-facing toggle on the Selection
  // Criteria tab. "auto" preserves the default length rule (≤4ft=1cct,
  // 5-6ft=2cct). "force_1cct" / "force_2cct" pins every ACB selection.
  const cctOverride = project?.advanced?.acb_circuit_override;
  payload.acb_circuit_override =
    (cctOverride === "force_1cct" || cctOverride === "force_2cct")
      ? cctOverride
      : "auto";

  const flowMode = project.water_flow_cap_mode || "off";
  const flowVal = parseFloat(project.water_flow_cap_value);
  if (flowMode === "max_gpm" && Number.isFinite(flowVal) && flowVal > 0) {
    payload.max_water_gpm = flowVal;
    payload.max_water_gpm_per_ton = null;
  } else if (flowMode === "max_gpm_per_ton" && Number.isFinite(flowVal) && flowVal > 0) {
    payload.max_water_gpm_per_ton = flowVal;
    payload.max_water_gpm = null;
  } else {
    payload.max_water_gpm = null;
    payload.max_water_gpm_per_ton = null;
  }

  return payload;
}

// ---- Build payload for /api/model-n/calculate ----
export function buildModelNPayload(project, room) {
  const dc = project.designConditions;
  const advanced = project.advanced || {};
  const isSummer = true;

  const payload = {
    oa_flow_cfm: parseFloat(room.oa_flow_cfm) || 0,
    outdoor_db_f: parseFloat(dc.outdoor_db_f),
    outdoor_wb_f: parseFloat(dc.outdoor_wb_f) || parseFloat(dc.outdoor_db_f) - 5,
    room_db_f: parseFloat(dc.room_db_f),
    room_rh: parseFloat(dc.room_rh),
    is_summer: isSummer,
    recovery_type: room.unit_model === "Model N-f" ? "None" : (advanced.recovery_type || "HRV"),
    water_entering_f: parseFloat(dc.water_entering_f) || 46,
    non_condensing_mode: !!advanced.non_condensing_mode,
    unit_type: room.unit_model,
    altitude_ft: parseFloat(dc.altitude_ft) || 0,
    // Models N / N-f ship as a SINGLE physical length: 3 ft only.
    // The manufacturer does not produce 4 ft N / N-f units.
    length_ft: 3,
  };

  if (room.cooling_total_btuh) {
    payload.target_total_capacity_btuh = parseFloat(room.cooling_total_btuh);
  }
  const sens = parseFloat(room.cooling_sensible_btuh);
  const total = parseFloat(room.cooling_total_btuh);
  if (sens > 0 && total > 0 && sens < total) {
    payload.target_sensible_capacity_btuh = sens;
  }

  // Heating block (added when project + room have the required inputs)
  const winterWaterTemp = parseFloat(dc.water_entering_winter_f);
  const winterOutdoorDb = parseFloat(dc.outdoor_db_winter_f);
  const winterRoomDb = parseFloat(dc.room_db_winter_f);
  const winterRoomRh = parseFloat(dc.room_rh_winter);
  const heatingTarget = parseFloat(room.heating_btuh);
  const hasWinterConditions = !isNaN(winterWaterTemp) && winterWaterTemp > 0
    && !isNaN(winterOutdoorDb) && !isNaN(winterRoomDb);
  if (hasWinterConditions) {
    payload.include_heating = true;
    payload.water_entering_winter_f = winterWaterTemp;
    payload.outdoor_db_winter_f = winterOutdoorDb;
    payload.room_db_winter_f = winterRoomDb;
    payload.room_rh_winter = isNaN(winterRoomRh) ? 30 : winterRoomRh;
    if (!isNaN(heatingTarget) && heatingTarget > 0) {
      payload.target_heating_btuh = heatingTarget;
    }
  }

  if (advanced.use_glycol && advanced.glycol_percentage) {
    payload.glycol_type = advanced.glycol_type || "PG";
    payload.glycol_pct = parseFloat(advanced.glycol_percentage);
  }

  return payload;
}

// ---- Run selection for one room. Returns {ok, summary, raw} ----

export async function runRoomSelection(project, room, optimize = false, forceQty = null, qtyMode = "divide", forceLength = null) {
  const model = room.unit_model;
  if (!model) {
    return { ok: false, error: "Unit Model not set" };
  }
  // Use room.qty as the default forceQty when no explicit override was passed.
  // This ensures the qty set in Room Details flows through on initial selection.
  const roomQty = parseInt(room.qty, 10);
  const fromRoomDetails = forceQty == null && !isNaN(roomQty) && roomQty > 1;
  const effectiveQty = forceQty != null ? forceQty : (fromRoomDetails ? roomQty : null);
  // Per Vladimir (2026-06-22): when QTY comes from Room Details (import or
  // initial entry), the entered cooling load is PER UNIT — never divide.
  // Each of the N units handles the full room cooling_total_btuh. Only
  // when the user changes QTY in Summary does the divide/duplicate modal
  // appear (handled by handleQtyCommit → updateRoomQty with explicit
  // forceQty + qtyMode).
  const effectiveQtyMode = fromRoomDetails ? "duplicate" : qtyMode;
  try {
    let raw, summary, plan;
    if (isModelN(model)) {
      const body = buildModelNPayload(project, room);
      const { data } = await axios.post(`${API}/model-n/calculate`, body);
      raw = data;
      summary = extractModelNSummary(data, room);
      plan = computeRoomUnitPlan(data, room, model, (project.advanced || {}).o2_circuit, null, effectiveQty, effectiveQtyMode, "room_load"); // AHRI 1335 single-basis lock
    } else {
      // ===== INITIAL PASS — canonical workflow (2026-05-21 spec): =====
      //   1. Water flow = MAX (catalog max).
      //   2. Run at user PA flow + SP. SP is informational only — capacity
      //      is f(PA flow, induction ratio, coil), so SP changes never
      //      change delivered capacity.
      //   3. If delivered_at_max ≥ target × 1.05 → modulate water flow
      //      DOWN to land in [target, target × 1.05].
      //   4. If delivered_at_max < target → keep max-flow result so the
      //      engineer sees the true under-delivery. Optimize button can
      //      then bump PA flow.
      //
      // `modulateWaterToWindow` encapsulates steps 1–4 and returns
      // {raw, summary}. It also respects the user's optional
      // advanced.water_flow_gpm cap (water-flow limitation = "active")
      // by leaving the cap in place rather than ramping to coil max.
      //
      // forceLength priority (highest → lowest):
      //   1. Explicit `forceLength` argument (from updateRoomQty when user has
      //      an overridden length — fixes V.23 cascade regression).
      //   2. room.__force_length (SingleUnitSelection per-length sweep).
      //   3. undefined → engine picks the best-fit length autonomously.
      const resolvedForceLength =
        forceLength != null && forceLength > 0 ? forceLength
        : (room?.__force_length != null && room.__force_length > 0 ? room.__force_length : undefined);
      const out = await modulateWaterToWindow(project, room, resolvedForceLength != null ? { forceLength: resolvedForceLength } : {});
      raw = out.raw;
      summary = out.summary;

      const totalRoom = parseFloat(room.cooling_total_btuh);
      const sensRoom = parseFloat(room.cooling_sensible_btuh);
      const useSens = !(totalRoom > 0) && sensRoom > 0;
      const target = totalRoom > 0 ? totalRoom : (useSens ? sensRoom : 0);
      const maxLenCap = (project?.advanced?.maxLengthByModel || {})[model];

      // ----- PA-flow bump loop for Primary-Air models (only when optimize=true) -----
      // Initial pass: skip this loop. User can click "Optimize" in the
      // Summary tab to enable PA-bump + cap-relax cascade.
      if (isPrimaryAir(model) && optimize) {
        // Use the SAME target resolution as above.
        const userPa = parseFloat(room.oa_flow_cfm) || 0;
        let curPa = userPa;
        let bumpCount = 0;
        const tolerance = 0.95;
        const useSensible = useSens;
        // Per-model Max-Length cap (already resolved above for the initial
        // probe). The bump loop operates within the cap; if exhausted, the
        // cap is relaxed and the bump retries against unrestricted lengths.
        let capActive = maxLenCap != null;
        // Use a fresh payload (with target) as the bump base so all calls
        // share consistent design conditions.
        const body = buildMultiLengthPayload(project, room);
        const meetsTarget = (d, capFt) => {
          // No target → nothing to bump.
          if (target <= 0) return true;
          let list = lengthCapacityList(d, model, null, "room_load"); // AHRI 1335 single-basis lock
          if (capFt != null) list = list.filter((l) => l.length_ft <= capFt);
          const longest = list[list.length - 1];
          if (!longest) return false;
          const capVal = useSensible ? (longest.max_sensible || 0) : (longest.max_total || 0);
          return capVal >= target * tolerance;
        };
        // Hard cap on bumps: 40 iterations (max +200 CFM); also bail
        // out if PA flow stops actually increasing (PA can't be bumped without
        // a positive base).
        while (!meetsTarget(raw, capActive ? maxLenCap : null) && bumpCount < 40 && curPa >= 0) {
          bumpCount += 1;
          const nextPa = (curPa || 0) + 5;
          if (nextPa === curPa) break;
          curPa = nextPa;
          const bumpBody = { ...body, pa_cfm: curPa };
          try {
            // eslint-disable-next-line no-await-in-loop
            const r = await axios.post(`${API}/calculate-multi-length`, bumpBody);
            raw = r.data;
          } catch (e) {
            break;
          }
        }
        // If still under-capacity after exhausting PA bumps within the cap,
        // relax the cap and try a few more bumps against the unrestricted
        // longest length. (Per user spec: "If still can't, increase length
        // and keep desired PA flow.")
        if (capActive && !meetsTarget(raw, maxLenCap)) {
          capActive = false;
          // Re-run with the user's original PA flow first (cap relaxed)
          if (curPa !== userPa) {
            curPa = userPa;
            try {
              // eslint-disable-next-line no-await-in-loop
              const r = await axios.post(`${API}/calculate-multi-length`, { ...body, pa_cfm: curPa });
              raw = r.data;
            } catch (e) { /* ignore */ }
          }
          // Final bump pass against unrestricted lengths in case longer
          // lengths still need a small PA top-up.
          let extra = 0;
          while (!meetsTarget(raw, null) && extra < 40 && curPa >= 0) {
            extra += 1;
            const nextPa = (curPa || 0) + 5;
            if (nextPa === curPa) break;
            curPa = nextPa;
            try {
              // eslint-disable-next-line no-await-in-loop
              const r = await axios.post(`${API}/calculate-multi-length`, { ...body, pa_cfm: curPa });
              raw = r.data;
            } catch (e) { break; }
          }
        }
        if (bumpCount > 0 || curPa !== userPa) {
          // If we relaxed the cap to satisfy capacity, drop the cap from this
          // selection so extractMultiLengthSummary picks the longer length.
          const projectForExtract = capActive ? project : {
            ...project,
            advanced: { ...(project?.advanced || {}), maxLengthByModel: { ...(project?.advanced?.maxLengthByModel || {}), [model]: null } },
          };
          summary = extractMultiLengthSummary(raw, room, projectForExtract);
          summary.pa_flow_cfm_used = curPa;
          summary.pa_flow_bumped_from = userPa;
          if (!capActive && maxLenCap != null) summary.max_length_cap_relaxed = maxLenCap;
        }
      }

    // Determine the active per-model length cap. When the cap was relaxed
    // (PA bump exhausted within cap and we fell back to longer lengths) we
    // drop the cap so the picker can select the longer length.
    let capForPlan = (project?.advanced?.maxLengthByModel || {})[model];
    if (isPrimaryAir(model) && summary?.max_length_cap_relaxed != null) {
      capForPlan = null;
    }
    plan = computeRoomUnitPlan(raw, room, model, (project.advanced || {}).o2_circuit, capForPlan, effectiveQty, effectiveQtyMode, "room_load"); // AHRI 1335 single-basis lock
  }

  // Multi-unit: re-run with FULL per-unit overrides (target/sens/heating/OA/EA)
    // so the summary reflects what ONE physical unit actually delivers.
    // Skip re-run when room has no OA specified (per_unit_oa == roomOa).
    const roomOa = parseFloat(room.oa_flow_cfm) || 0;
    if (plan.qty > 1 && roomOa > 0) {
      const qty = plan.qty;
      const totalRoom = parseFloat(room.cooling_total_btuh) || 0;
      const ratio =
        totalRoom > 0 && plan.per_unit_target ? plan.per_unit_target / totalRoom : 1 / qty;
      const senRoom = parseFloat(room.cooling_sensible_btuh);
      const heatRoom = parseFloat(room.heating_btuh);
      const eaRoom = parseFloat(room.ea_flow_cfm);
      // Duplicate mode = each unit handles FULL room loads (heat/EA stay 1:1).
      // Divide mode    = original behaviour (heat/EA scaled with qty).
      const duplicate = plan.qty_mode === "duplicate" || qtyMode === "duplicate";
      const heatPerUnit = !isNaN(heatRoom) && heatRoom > 0
        ? (duplicate ? heatRoom : heatRoom / qty)
        : null;
      const eaPerUnit = !isNaN(eaRoom) && eaRoom > 0
        ? (duplicate ? eaRoom : Math.ceil(eaRoom / qty))
        : null;

      const sub = await runSubUnitSelection(project, room, {
        model,
        length_ft: plan.length_ft,
        per_unit_target: plan.per_unit_target,
        per_unit_sens: !isNaN(senRoom) && senRoom > 0 ? senRoom * ratio : null,
        per_unit_heating: heatPerUnit,
        per_unit_oa: plan.per_unit_oa,
        per_unit_ea: eaPerUnit,
      });
      if (sub.ok) {
        const subTotal = parseFloat(sub.summary?.cooling_total_btuh) || plan.per_unit_total;
        summary = {
          ...sub.summary,
          plan: {
            ...plan,
            per_unit_total: subTotal,
            units: [
              {
                model,
                length_ft: plan.length_ft,
                qty: plan.qty,
                per_unit_target: plan.per_unit_target,
                per_unit_total: subTotal,
                per_unit_oa: plan.per_unit_oa,
                result: sub,
              },
            ],
          },
        };
        raw = sub.raw;
      } else {
        // Re-run failed: keep initial summary + plan
        summary.plan = plan;
      }
    } else {
      summary.plan = plan;
    }

    // Reorder data.results so the chosen length is first (multi-length models)
    if (!isModelN(model) && plan.length_ft && Array.isArray(raw?.results)) {
      const idx = raw.results.findIndex((r) => r.length_ft === plan.length_ft);
      if (idx > 0) {
        const picked = raw.results[idx];
        raw.results = [picked, ...raw.results.filter((_, i) => i !== idx)];
      }
      // Re-extract summary at the plan's chosen length so the Summary tab's
      // capacity numbers (cooling_total_btuh, heating_btuh, etc.) match the
      // Detail view's banner. Without this, the initial extract used the
      // picker's shortest-meets-target heuristic which can disagree with
      // computeRoomUnitPlan's length choice for ACB / multi-length models.
      // Preserve summary.plan across the re-extract — plan is the source of
      // truth for length/qty and must not be lost.
      const reExtracted = extractMultiLengthSummary(raw, room, project, plan.length_ft);
      summary = { ...reExtracted, plan: summary.plan };
      summary.selected_length_ft = plan.length_ft || summary.selected_length_ft;
    }

    return { ok: true, raw, summary };
  } catch (err) {
    return {
      ok: false,
      error: formatApiError(err),
    };
  }
}

function extractModelNSummary(data, room) {
  const ai = data?.summary_table?.air_side || {};
  const ws = data?.summary_table?.water_side || {};
  const mf = data?.modulated_flow || {};
  // Model N has no PA, so the SummaryTab's Total Sens/Lat columns (which sum
  // pa_sens + coil_sens / pa_lat + coil_lat) need coil_*_btuh aliases for the
  // numbers to appear. cooling_*_btuh stays for downstream consumers that
  // already read it (Detail banner, PDF report).
  const sens = ai.sensible_capacity_btuh ?? mf.achieved_sensible_btuh ?? 0;
  const lat = ai.latent_capacity_btuh ?? mf.achieved_latent_btuh ?? 0;
  return {
    oa_cfm: data?.system_parameters?.oa_flow_cfm ?? room.oa_flow_cfm,
    ea_cfm: room.ea_flow_cfm || null,
    cooling_total_btuh: ai.total_capacity_btuh ?? mf.achieved_capacity_btuh,
    cooling_sensible_btuh: sens,
    cooling_latent_btuh: lat,
    coil_sensible_btuh: sens,
    coil_latent_btuh: lat,
    heating_btuh: data?.heating?.achieved_capacity_btuh ?? null,
    nc_level: ai.nc_level || null,
    primary_air_cfm: ai.pac_airflow_cfm ?? mf.primary_air_cfm,
    selected_length_ft: data?.system_parameters?.length_ft || null,
    water_flow_gpm: ws.total_water_flow_gpm ?? mf.pac_flow_gpm ?? null,
    water_pd_fth2o: ws.total_water_pd_fth2o ?? null,
    heating_water_flow_gpm: data?.heating?.summary_table?.water_side?.total_water_flow_gpm ?? null,
    heating_water_pd_fth2o: data?.heating?.summary_table?.water_side?.total_water_pd_fth2o ?? null,
  };
}

// ---------------------------------------------------------------------------
// extractRoomDelivered — canonical "delivered to the room" record for one
// length-result entry.  Single source of truth shared by:
//   - extractMultiLengthSummary (Summary tab)
//   - RoomReportPage.computeDeliveredCapacity (Detail banner, non-PA branch)
//
// rawResult : one entry from raw.results[N] (a single length entry from the
//             multi-length endpoint), i.e. what extractMultiLengthSummary calls "best".
// project   : full project object (for cascade speed resolution + design conds).
// room      : room object (for oa_flow_cfm, cooling targets, unit_model).
//
// Returns an internal record with these fields:
//   total, sensible, latent       — cooling (BTU/h), null when opt is absent
//   heating                       — heating BTU/h (0 if none)
//   water_flow_gpm, water_pd_fth2o
//   heating_water_flow_gpm, heating_water_pd_fth2o
//   airflow                       — total supply CFM
//   basis                         — "water_side" | "room_load"
//   is_pa                         — true for induction / PA models
//   nc_level
//   selected_length_ft, selected_circuits
//   nozzle_label
//   pa_sensible, pa_latent        — PA contribution (0 for non-PA)
//   coil_sensible, coil_latent    — coil-only contribution
//
// NO unmodulated chosenSpeed.* fallbacks for capacity or PD (B.7).
// ---------------------------------------------------------------------------
export function extractRoomDelivered(rawResult, project, room) {
  const best = rawResult || {};
  const { speedRow: chosenSpeed, opt, basis } = pickCascadeOperatingPoint(best, project);
  const speeds = best.cooling?.speeds || [];
  const airSide = chosenSpeed.air_side || {};

  const isPA = best.cooling?.pa_mode === true ||
    (speeds.length === 1 && speeds[0]?.speed === "pa");
  const paSpeed = isPA ? (speeds[0] || {}) : null;

  const dc = project?.designConditions || {};
  const T_room = parseFloat(dc.room_db_f) || 75;
  const RH_room = parseFloat(dc.room_rh) || 50;
  const T_pa = parseFloat(dc.pa_eat_db_f) || parseFloat(dc.outdoor_db_f) || 95;
  const RH_pa = parseFloat(dc.pa_eat_rh) || 50;
  const paCfm = isPA
    ? (parseFloat(paSpeed?.primary_cfm) || parseFloat(best.cooling?.pa_cfm) || parseFloat(room?.oa_flow_cfm) || 0)
    : 0;

  // Raw psychrometric PA contribution — pure physics, model-agnostic.
  const paSensRaw = isPA ? paSensibleCapacity(paCfm, T_room, T_pa) : 0;
  const paLatRaw  = isPA ? paLatentCapacity(paCfm, T_room, RH_room, T_pa, RH_pa) : 0;

  // Model D is a smaller physical unit than Model C (same coil data, derated).
  // The backend scales coil fields via _MODEL_D_CAPACITY_KEYS but does NOT
  // include pa_total_btuh_input or the psychro-computed PA contribution in that
  // whitelist — so the frontend must derate PA here to stay in sync.
  const isModelD = room?.unit_model === "Model D";
  const paDerate = isModelD ? MODEL_D_CAPACITY_DERATE : 1.0;
  const paSens = paSensRaw * paDerate;
  const paLat  = paLatRaw  * paDerate;

  // PA models: read room-delivered totals from opt.final_*_btuh, which the
  // backend stamps as PA_sens + coil_sens (see primary_air_calculator.py
  // build_pa_result + server.py PA post-process at ~line 3866). This matches
  // the per-room detail table (PA Sens + Coil Sens → Total Sens) and the
  // physically-correct room-delivered cooling.
  //
  // Bug fix 2026-06-27 (Vladimir): previously used opt.air_*_btuh, which is
  // populated upstream by compute_supply_from_mixing — a separate code path
  // that can diverge from PA+coil composition (e.g., when primary_db_f isn't
  // set, the mixing function treats PA as if it entered at the coil-entering
  // temp, giving wrong supply-vs-room sensible). The green banner showed
  // ~1,080 W where the detail showed 1,682 W on a real Model C1-10ft room.
  // Fall back to opt.air_*_btuh for cached/legacy responses that pre-date
  // the backend's final_* stamping.
  //
  // Model D: backend derates final_* fields via _MODEL_D_CAPACITY_KEYS so
  // they arrive already derated; paDerate is only applied to the psychro
  // fallback below for older payloads.
  //
  // DRY-COIL: the engine internally applies the 2°F safety band when
  // EWT > (room_dewpoint − 2) — it forces the WATER-SIDE coil latent to 0
  // inside water_cap_at_flow, then build_pa_result composes final_latent
  // accordingly. Do NOT re-apply dry-coil logic in the frontend.
  // For PA (induction) models, air_*_btuh is the room-delivered Q_r (PA + coil air-side).
  // final_*_btuh is the water-side Q_c; never use it for banner/display on PA models.
  // Backend Fix 1 ensures air_*_btuh is populated correctly on the optimal_point.
  let total    = isPA ? (opt.air_total_btuh ?? null)    : (opt.final_total_btuh ?? null);
  let sensible = isPA ? (opt.air_sensible_btuh ?? null) : (opt.final_sensible_btuh ?? null);
  let latent   = isPA ? (opt.air_latent_btuh ?? null)   : (opt.final_latent_btuh ?? null);

  // Non-PA dry-coil only (FACB / single-coil families): blank the latent so
  // downstream tables don't display a misleading wet-coil number.
  const T_water_in = parseFloat(dc.water_entering_f) || 45;
  const T_C_dp = (T_room - 32) * 5 / 9;
  const a_dp = 17.27, b_dp = 237.3;
  const alpha_dp = (a_dp * T_C_dp) / (b_dp + T_C_dp) + Math.log(Math.max(RH_room / 100, 0.0001));
  const dp_C = (b_dp * alpha_dp) / (a_dp - alpha_dp);
  const roomDewpoint = dp_C * 9 / 5 + 32;
  const dryCoil = T_water_in > (roomDewpoint - 2);
  if (!isPA && dryCoil) latent = null;

  // Per-component breakdown for downstream tables.
  // PA sensible/latent: prefer backend opt fields (pre-derated for Model D);
  // fall back to frontend psychro recompute only when absent (old saved data).
  const pa_sensible = isPA
    ? (opt.pa_sensible_btuh != null ? opt.pa_sensible_btuh : paSens)
    : 0;
  const pa_latent = isPA
    ? (opt.pa_latent_btuh != null ? opt.pa_latent_btuh : paLat)
    : 0;
  // coil_* = delivered total minus PA contribution (not raw water-side coil heat removal).
  // Clamp to >= 0 to absorb minor rounding noise.
  //
  // DRY-COIL ZERO: when EWT > room dewpoint − 2°F (dryCoil flag computed
  // above) the water-side coil cannot condense moisture — physically there
  // is NO coil latent. compute_supply_from_mixing (mass-balance) and
  // compute_pa_load (enthalpy) use slightly different humidity-ratio
  // formulas, so opt.air_latent − opt.pa_latent can leak a few hundred
  // BTU/h of phantom "coil latent" on a coil that's actually dry. Force
  // coil_latent = 0 in that regime so the schedule + detail PDFs don't
  // misrepresent a dry coil as wet.
  const coil_sensible = isPA ? Math.max(0, (sensible ?? 0) - pa_sensible) : (sensible ?? null);
  const coil_latent = isPA
    ? (dryCoil ? 0 : Math.max(0, (latent ?? 0) - pa_latent))
    : (latent ?? null);

  // Heating.
  const heatingOptimal = best.heating_optimal || {};
  const heatSpeeds = best.heating?.speeds || [];
  const _heatRow =
    heatSpeeds.find((s) => s.speed === "med") ||
    heatSpeeds.find((s) => s.speed === "high") ||
    heatSpeeds[heatSpeeds.length - 1] ||
    null;
  const heating =
    heatingOptimal.air_total_btuh ||
    heatingOptimal.total_btuh ||
    _heatRow?.final_total_btuh ||
    0;
  // PA contribution to heating (positive = PA warms room, negative = PA cools room).
  // Provided by the backend as heating_optimal.pa_total_btuh_input.
  // Net room heating = coil air-side (heating) + pa_heat_contribution.
  // pa_total_btuh_input is NOT in backend _MODEL_D_CAPACITY_KEYS so it arrives
  // un-derated even for Model D — apply the same factor here.
  const pa_heat_contribution =
    (parseFloat(heatingOptimal.pa_total_btuh_input) || 0) * paDerate;

  // Airflow.
  const airTotalCfm =
    (airSide?.total_supply_cfm ??
      chosenSpeed.total_supply_cfm ??
      chosenSpeed.supply_cfm ??
      chosenSpeed.cfm ??
      parseFloat(room?.oa_flow_cfm)) ||
    0;

  // Water side — only opt and top-level result; never speed-row.
  const water_flow_gpm =
    opt.water_flow_gpm ??
    best.water_flow_gpm ??
    null;
  const water_pd_fth2o =
    opt.water_pd_fth2o ??
    opt.total_pd_fth2o ??
    best.water_pd_fth2o ??
    null;
  const heating_water_flow_gpm =
    heatingOptimal.water_flow_gpm ??
    _heatRow?.water_flow_gpm ??
    _heatRow?.total_water_flow_gpm ??
    best.heating?.water_flow_gpm ??
    null;
  const heating_water_pd_fth2o =
    heatingOptimal.water_pd_fth2o ??
    _heatRow?.water_pd_fth2o ??
    _heatRow?.total_water_pd_fth2o ??
    best.heating?.total_pd_fth2o ??
    null;

  const nozzle_label =
    paSpeed?.nozzle_label ??
    paSpeed?.nozzle ??
    chosenSpeed.nozzle_label ??
    chosenSpeed.nozzle ??
    best.nozzle_label ??
    best.nozzle ??
    null;

  return {
    total,
    sensible,
    latent,
    pa_sensible,
    pa_latent,
    coil_sensible: coil_sensible ?? 0,
    coil_latent: coil_latent ?? 0,
    heating,
    pa_heat_contribution,
    airflow: airTotalCfm,
    water_flow_gpm,
    water_pd_fth2o,
    heating_water_flow_gpm,
    heating_water_pd_fth2o,
    basis,
    is_pa: isPA,
    nc_level: chosenSpeed.nc_level || best.nc_level || null,
    selected_length_ft: best.length_ft || null,
    selected_circuits: best.circuits || null,
    nozzle_label,
  };
}

export function extractMultiLengthSummary(data, room, project, forceLength, forceCircuits) {
  // Pick best matching length: lowest length that meets target, or shortest if no target.
  // `forceLength` (optional): when set, pick the result with that exact length_ft
  // regardless of capacity matching. Used by the Summary tab's length-override
  // dropdown so the user can manually switch unit length per room.
  // `forceCircuits` (optional): when set alongside forceLength, further constrains
  // the row to a specific circuit count (for models that have both 1-ckt and 2-ckt).
  const results = data?.results || [];
  const tgtTot = parseFloat(room.cooling_total_btuh) || 0;
  const tgtSen = parseFloat(room.cooling_sensible_btuh) || 0;
  const useSensible = !(tgtTot > 0) && tgtSen > 0;
  const target = useSensible ? tgtSen : tgtTot;
  // Per-model Max-Length cap from the Advanced tab (applies to non-EWS models).
  // Map shape: project.advanced.maxLengthByModel = { "Model C": 4, ... }.
  // forceLength overrides the cap (user manually picked a length).
  const advancedMax = project?.advanced?.maxLengthByModel || {};
  const maxLenCap = forceLength != null ? null : advancedMax[room.unit_model];
  const sorted = [...results]
    .filter((r) => maxLenCap == null || (r.length_ft != null && r.length_ft <= maxLenCap))
    .sort((a, b) => (a.length_ft || 0) - (b.length_ft || 0));

  // Helper to get capacity from a result row for the picker's "meets target"
  // test. CRITICAL: this must return the MODULATED, room-delivered value —
  // the same number extractRoomDelivered will later read for display.
  //
  // Prior bug: reading speeds[0].final_total_btuh (full-water, unmodulated)
  // overstated capacity → picker would select an under-sized length (e.g. L=2
  // with full-water 3,063 passing 2710 target, but modulated optimal only
  // 2,361 → user saw 2,361 even though display labelled "Model H-4"). This
  // mirrors lengthCapacityList's room_load fallback (opt → speeds → final).
  const getMaxCooling = (r) => {
    const opt = r?.optimal_point || {};
    const speeds = r?.cooling?.speeds || [];
    const highSpeed = speeds.find((s) => s.speed === "high") || speeds[speeds.length - 1] || {};
    if (useSensible) {
      return (
        opt.air_sensible_btuh ??
        r.air_sensible_btuh ??
        highSpeed.air_side?.air_sensible_btuh ??
        highSpeed.final_sensible_btuh ??
        r.final_sensible_btuh ??
        0
      );
    }
    return (
      opt.air_total_btuh ??
      r.air_total_btuh ??
      highSpeed.air_side?.air_total_btuh ??
      highSpeed.final_total_btuh ??
      r.final_total_btuh ??
      0
    );
  };

  let forced = null;
  if (forceLength != null) {
    // When forceCircuits is also specified, match both; otherwise match length only.
    forced = forceCircuits != null
      ? sorted.find((r) => r.length_ft === forceLength && r.circuits === forceCircuits)
      : sorted.find((r) => r.length_ft === forceLength);
  }
  const picked =
    forced ||
    (target > 0
      ? sorted.find((r) => getMaxCooling(r) >= target * 0.95)
      : null);
  // If no length meets the target, fall back to the LONGEST (highest capacity).
  // If no target at all, default to shortest unit.
  const best =
    picked ||
    (target > 0 ? sorted[sorted.length - 1] : sorted[0]) ||
    {};
  
  // Delegate all capacity / PD / heating math to the shared helper so the
  // Summary tab and Detail banner always read the same fields in the same order.
  const d = extractRoomDelivered(best, project, room);

  const coilSens = d.coil_sensible || 0;
  const coilLat  = d.coil_latent  || 0;
  // coil_total_btuh: Qc = coil sens + coil lat (water-side chiller load).
  // Used by SummaryTab's Delivered % when capacity_basis == "water_side".
  const coilTotal = coilSens + coilLat;
  // coil_heating_btuh: water-side coil heat only (no PA contribution).
  // For non-PA models d.heating is already coil-only (heatingOptimal.air_total_btuh).
  // For PA models d.heating includes the PA heat contribution, so we strip it back.
  const coilHeating = d.is_pa
    ? Math.max(0, (d.heating ?? 0) - (d.pa_heat_contribution ?? 0))
    : (d.heating ?? 0);

  return {
    oa_cfm: room.oa_flow_cfm,
    air_total_cfm: d.airflow || null,
    ea_cfm: room.ea_flow_cfm || null,
    // Preserved field names — backward-compat with saved-project JSON (B.7).
    cooling_total_btuh:    d.total,
    cooling_sensible_btuh: d.sensible,
    cooling_latent_btuh:   d.latent,
    pa_sensible_btuh:      d.pa_sensible,
    pa_latent_btuh:        d.pa_latent,
    coil_sensible_btuh:    coilSens,
    coil_latent_btuh:      coilLat,
    // Qc breakdown: coil-only totals — retained for plant-sizing reference.
    coil_total_btuh:       coilTotal,
    coil_heating_btuh:     coilHeating,
    // AHRI 1335 single-basis lock: capacity_basis always room_load.
    // d.basis is echoed from backend; fallback guards stale cached responses.
    capacity_basis:        d.basis || "room_load",
    is_pa_model:           d.is_pa,
    heating_btuh:          d.heating || null,
    heating_pa_btuh:       d.pa_heat_contribution || 0,
    nc_level:              d.nc_level,
    selected_length_ft:    d.selected_length_ft,
    selected_circuits:     d.selected_circuits,
    circuit_selection:     best.circuit_selection ?? null,
    water_flow_gpm:        d.water_flow_gpm,
    water_pd_fth2o:        d.water_pd_fth2o,
    heating_water_flow_gpm: d.heating_water_flow_gpm,
    heating_water_pd_fth2o: d.heating_water_pd_fth2o,
    nozzle_label:           d.nozzle_label,
  };
}

// -------------------------------------------------------------------
// SUB-UNIT SELECTION for multi-unit overrides.
//
// Given a project + reference room + a desired (model, length_ft, per_unit_target),
// run the correct backend endpoint to get a selection for ONE sub-unit.
//
// Returns { ok, raw, summary, max_capacities_by_length }
// where max_capacities_by_length is [{length_ft, max_total}] for the model
// (useful for populating the override editor's length dropdown).
// -------------------------------------------------------------------
export async function runSubUnitSelection(project, refRoom, override) {
  const {
    model, length_ft,
    per_unit_target, per_unit_sens, per_unit_heating,
    per_unit_oa, per_unit_ea,
  } = override;
  if (!model) return { ok: false, error: "Model required" };

  // Build a synthetic room with ALL per-unit overrides applied.
  // EA is meaningful only for Model N / N-f — zero it for other models.
  const isN = isModelN(model);
  const subRoom = {
    ...refRoom,
    unit_model: model,
    cooling_total_btuh:
      per_unit_target != null ? String(per_unit_target) : refRoom.cooling_total_btuh,
    cooling_sensible_btuh:
      per_unit_sens != null ? String(per_unit_sens) : refRoom.cooling_sensible_btuh,
    heating_btuh:
      per_unit_heating != null ? String(per_unit_heating) : refRoom.heating_btuh,
    oa_flow_cfm:
      per_unit_oa != null ? String(per_unit_oa) : refRoom.oa_flow_cfm,
    ea_flow_cfm: isN
      ? (per_unit_ea != null ? String(per_unit_ea) : refRoom.ea_flow_cfm)
      : "",
  };
  // Re-derive latent if total + sens are both set (kept in sync for display)
  const t = parseFloat(subRoom.cooling_total_btuh);
  const s = parseFloat(subRoom.cooling_sensible_btuh);
  if (!isNaN(t) && !isNaN(s) && t >= s) {
    subRoom.cooling_latent_btuh = String(t - s);
  }

  try {
    if (isModelN(model)) {
      const body = buildModelNPayload(project, subRoom);
      const { data } = await axios.post(`${API}/model-n/calculate`, body);
      const summary = extractModelNSummary(data, subRoom);
      const maxCaps = lengthCapacityList(data, model, null, "room_load"); // AHRI 1335 single-basis lock
      return { ok: true, raw: data, summary, max_capacities_by_length: maxCaps };
    }

    const body = buildMultiLengthPayload(project, subRoom);
    if (length_ft) body.length_ft = parseFloat(length_ft);
    const { data } = await axios.post(`${API}/calculate-multi-length`, body);
    const summary = extractMultiLengthSummary(data, subRoom, project);
    const maxCaps = lengthCapacityList(data, model, (project.advanced || {}).o2_circuit, "room_load"); // AHRI 1335 single-basis lock

    // If a specific length was requested, reorder results so it's first.
    if (length_ft && Array.isArray(data?.results)) {
      const idx = data.results.findIndex((r) => r.length_ft === parseFloat(length_ft));
      if (idx > 0) {
        const picked = data.results[idx];
        data.results = [picked, ...data.results.filter((_, i) => i !== idx)];
        summary.selected_length_ft = picked.length_ft;
      }
    }
    return { ok: true, raw: data, summary, max_capacities_by_length: maxCaps };
  } catch (err) {
    return {
      ok: false,
      error: formatApiError(err),
    };
  }
}

// Fetch the max-capacity curve for a given model at the project's design
// conditions (without forcing a target). Used to populate the Max-Capacity
// reference table in the Summary override UI.
export async function fetchMaxCapacityMatrix(project, refRoom, model) {
  const probeRoom = { ...refRoom, unit_model: model, cooling_total_btuh: "" };
  try {
    if (isModelN(model)) {
      const body = buildModelNPayload(project, probeRoom);
      const { data } = await axios.post(`${API}/model-n/calculate`, body);
      return lengthCapacityList(data, model, null, "room_load"); // AHRI 1335 single-basis lock
    }
    const body = buildMultiLengthPayload(project, probeRoom);
    const { data } = await axios.post(`${API}/calculate-multi-length`, body);
    return lengthCapacityList(data, model, (project.advanced || {}).o2_circuit, "room_load"); // AHRI 1335 single-basis lock
  } catch (err) {
    return [];
  }
}

// -------------------------------------------------------------------
// Iterative water-flow bump for a SPECIFIC length (user override).
//
// When the user picks a length from the Summary dropdown, we re-run the
// same modulation iteration as the initial pass but scoped to that one
// length. Returns { raw, summary } — the caller wires these into the
// room's result object the same way `extractMultiLengthSummary` does.
//
// `forceLength` is in IP feet. Used to compute the max-capacity reference
// from the cached probe data, and to force `extractMultiLengthSummary` to
// pick that specific length when building the summary.
// -------------------------------------------------------------------
// =====================================================================
// Water-flow modulation (canonical implementation)
// =====================================================================
//
// Engineer's workflow (confirmed 2026-05-21):
//
//   1. Water flow = MAX (catalog max for the selected coil at the
//      selected length).
//   2. Run selection at the user's PA flow + SP. SP is *informational*
//      only — capacity is f(PA flow, fixed induction ratio, coil) so
//      the SP value never changes delivered capacity.
//   3. If delivered_capacity_at_max ≥ target × 1.05 → modulate water
//      flow DOWN until the delivered capacity lands in
//      [target, target × 1.05]. Stop as soon as we're inside that
//      window.
//   4. If delivered_capacity_at_max < target → keep the max-water
//      result (best effort). Engineer can then click Optimize to bump
//      PA flow or relax length cap.
//
// The backend's "target_cooling_btuh + omit water_flow_gpm" path is the
// hook we use to push the engine toward a desired delivered capacity:
// it runs an internal water-flow optimisation to find the *lowest*
// flow that meets `target_cooling_btuh`. So to throttle DOWN from max
// we ask for `target × 1.025` (mid-window) — the backend then sizes
// water flow to deliver ~1.025× target, which sits squarely in our
// acceptance window.
//
// `forceLength` (optional): when set the modulator forces the engine
// to evaluate at that specific length (and pins extraction onto that
// length too). The Summary tab's per-row length dropdown uses this so
// the chosen length is honoured even when capacity is short.
async function modulateWaterToWindow(project, room, opts = {}) {
  const { forceLength = null } = opts;
  const model = room.unit_model;
  const totalRoom = parseFloat(room.cooling_total_btuh);
  const sensRoom = parseFloat(room.cooling_sensible_btuh);
  const useSens = !(totalRoom > 0) && sensRoom > 0;
  const target = totalRoom > 0 ? totalRoom : (useSens ? sensRoom : 0);

  // Rule 2/3 (V.20): backend now handles all modulation — water ceiling + speed
  // step-down — when target is included in the payload. The two-call mid-window
  // probe pattern (max probe → modulate to target×1.025) is replaced by a single
  // call with the target. The backend:
  //   • Rule 2 (no user water cap): pins water at the per-(model,length) ceiling
  //     and steps speed DOWN (MED→LOW) if over-delivering by > 5%.
  //   • Rule 3 (user water_flow_gpm supplied): bisects water within [0.05, user_cap];
  //     if water hits the floor and still over-delivers, steps speed down.
  //
  // "Max free-run" callers (no target, omitTarget=true) continue to work as before
  // and are handled by the early-exit path below.

  if (target <= 0) {
    // No target — run at max water flow with omitTarget so backend returns
    // full 3-speed table without modulation.
    const maxBody = buildMultiLengthPayload(project, room, { omitTarget: true });
    if (forceLength != null) maxBody.length_ft = forceLength;
    const { data: maxData } = await axios.post(`${API}/calculate-multi-length`, maxBody);
    return {
      raw: maxData,
      summary: extractMultiLengthSummary(maxData, room, project, forceLength),
    };
  }

  // Single call with target. Backend performs Rule 2 or Rule 3 modulation
  // and returns optimal_point with the chosen speed and water flow.
  const body = buildMultiLengthPayload(project, room);
  if (forceLength != null) body.length_ft = forceLength;
  const { data } = await axios.post(`${API}/calculate-multi-length`, body);
  return {
    raw: data,
    summary: extractMultiLengthSummary(data, room, project, forceLength),
  };
}

export async function bumpForLength(project, room, forceLength) {
  const model = room.unit_model;
  if (!model) return null;
  if (isModelN(model)) return null; // EWS family — handled by Model-N endpoint
  return modulateWaterToWindow(project, room, { forceLength });
}

// Re-run selection with a user-supplied water flow (GPM) override.
// Sends the given GPM as `water_flow_gpm` in the payload so the backend
// treats it as a ceiling (Rule 3 path). Returns {raw, summary} or null.
//
// Pins acb_strategy = "given_flow_given_sp" on the call so the engine
// honors the typed value verbatim. Without this, projects configured
// with a "*_lowest_*" strategy would sweep the user's value downward
// looking for a smaller flow/SP — meaning two identical edits could
// return different capacities (the symptom is "0.4 → 0.4 → worse").
//
// `lockLength` (optional): when set, restricts the backend to that single
// length via selected_lengths. Without this, the auto-picker on the
// response side can silently migrate the row to a different length
// (e.g. user typed 2.4 on a Model H-3 row → engine met target at L=4 →
// row displayed "Model H-3" but the values were L=4's).
export async function bumpForWaterFlow(project, room, forceWaterGpm, lockLength = null) {
  const model = room.unit_model;
  if (!model) return null;
  if (isModelN(model)) return null;
  const body = buildMultiLengthPayload(project, room);
  body.water_flow_gpm = parseFloat(forceWaterGpm);
  body.acb_strategy = "given_flow_given_sp";
  if (lockLength != null && lockLength > 0) body.selected_lengths = [parseInt(lockLength, 10)];
  const { data } = await axios.post(`${API}/calculate-multi-length`, body);
  return {
    raw: data,
    summary: extractMultiLengthSummary(data, room, project, lockLength),
  };
}

// Re-run selection with a user-supplied PA flow (CFM) override.
// Sends the value as pa_cfm (the actual backend PA-stream field for
// induction models). ACB-only — FACB models have no engineer-tunable PA.
// Pins acb_strategy = "given_flow_given_sp" — see bumpForWaterFlow.
export async function bumpForPAFlow(project, room, forcePaCfm, lockLength = null) {
  const model = room.unit_model;
  if (!model) return null;
  if (isModelN(model)) return null;
  const body = buildMultiLengthPayload(project, room);
  const cfm = parseFloat(forcePaCfm);
  if (!Number.isFinite(cfm) || cfm < 0) return null;
  body.pa_cfm = cfm;
  body.oa_flow_cfm = cfm;
  body.acb_strategy = "given_flow_given_sp";
  if (lockLength != null && lockLength > 0) body.selected_lengths = [parseInt(lockLength, 10)];
  const { data } = await axios.post(`${API}/calculate-multi-length`, body);
  return {
    raw: data,
    summary: extractMultiLengthSummary(data, room, project, lockLength),
  };
}

// Re-run selection with a user-supplied PA static pressure (in.wg) override.
// Only meaningful for ACB / FACB models that accept pa_static_pressure_inwg.
// Returns {raw, summary} or null.
//
// See bumpForWaterFlow for why acb_strategy is pinned here.
export async function bumpForStaticPressure(project, room, forceSpInwg, lockLength = null) {
  const model = room.unit_model;
  if (!model) return null;
  if (isModelN(model)) return null;
  const body = buildMultiLengthPayload(project, room);
  body.pa_static_pressure_inwg = parseFloat(forceSpInwg);
  body.acb_strategy = "given_flow_given_sp";
  if (lockLength != null && lockLength > 0) body.selected_lengths = [parseInt(lockLength, 10)];
  const { data } = await axios.post(`${API}/calculate-multi-length`, body);
  return {
    raw: data,
    summary: extractMultiLengthSummary(data, room, project, lockLength),
  };
}

// Re-run selection with a user-supplied spigot bore override (inches).
// ACB-only — FACB / EWS models have no spigot concept. NC for the row
// updates live; for spigot-dependent models (C/C1/D) the NC may change
// dramatically (small-bore = loud); for flow-only ACBs (H/I-*/L-*) NC
// stays the same — that's an explicit acceptance behavior, not a bug.
// Length is locked (see bumpForWaterFlow) so editing the bore never
// silently migrates the row to a different length.
export async function bumpForSpigot(project, room, boreIn, lockLength = null) {
  const model = room.unit_model;
  if (!model) return null;
  if (isModelN(model)) return null;
  const bore = parseInt(boreIn, 10);
  if (!Number.isFinite(bore) || ![4, 6, 8, 10].includes(bore)) return null;
  const body = buildMultiLengthPayload(project, room);
  body.acb_spigot_bore_in = bore;
  body.acb_strategy = "given_flow_given_sp";
  if (lockLength != null && lockLength > 0) body.selected_lengths = [parseInt(lockLength, 10)];
  const { data } = await axios.post(`${API}/calculate-multi-length`, body);
  return {
    raw: data,
    summary: extractMultiLengthSummary(data, room, project, lockLength),
  };
}

// -----------------------------------------------------------------------
// formatSizingFootnote — renders a plain-text sizing-method footnote for
// use in PDF reports (Phase B.4) and Detail pages (Phase B.3).
//
// Accepts an optional `rooms` array; when provided, only the family lines
// relevant to the rooms present are included. When rooms is omitted, both
// FACB and ACB lines are rendered (safe fallback for callers that don't
// have rooms context).
//
// EWS-only projects skip both strategy lines (no fan / water strategy
// applies to Model N / N-f).
//
// Examples:
//   FACB-only, water_side + lowest_speed (no cap):
//     "Sizing method — Capacity basis: Water-side coil capacity (Q_c).
//      FACB strategy: Lowest fan speed at modulated water flow (max auto).
//      Reference: AHRI 1335."
//
//   ACB-only, water_side + given_flow_given_sp:
//     "Sizing method — Capacity basis: Water-side coil capacity (Q_c).
//      ACB strategy: Given PA flow at given static pressure.
//      Reference: AHRI 1335."
// -----------------------------------------------------------------------
export function formatSizingFootnote(project, rooms) {
  // AHRI 1335 single-basis lock: capacity_basis is always room_load.
  // The "Water-side coil capacity (Q_c)" branch is dead code — kept for
  // backend direct-caller compatibility only.
  const basis = "room_load"; // hardcoded; UI selector removed
  const facbStrategy = project?.facb_strategy || "lowest_speed";
  const acbStrategy = project?.acb_strategy || "given_flow_given_sp";
  const flowGpm = parseFloat(project?.advanced?.water_flow_gpm);
  const hasFlow = !isNaN(flowGpm) && flowGpm > 0;

  const basisText = "Room load (AHRI 1335 — air-side)";

  // Determine which families are present.
  const families = getProjectFamilies(rooms);
  // When rooms is omitted (undefined/null), include both FACB and ACB lines.
  const noRoomsCtx = rooms == null;
  const showFACB = noRoomsCtx ? true : families.hasFACB;
  const showACB = noRoomsCtx ? true : families.hasACB;
  const ewsOnly = !noRoomsCtx && families.hasEWS && !families.hasFACB && !families.hasACB;

  // FACB strategy line.
  let facbText = null;
  if (showFACB) {
    if (facbStrategy === "given_flow") {
      facbText = hasFlow
        ? `FACB strategy: Given water flow, fixed at ${flowGpm.toFixed(2)} GPM.`
        : "FACB strategy: Given water flow, fixed at specified GPM.";
    } else {
      facbText = hasFlow
        ? `FACB strategy: Lowest fan speed at modulated water flow (max ${flowGpm.toFixed(2)} GPM).`
        : "FACB strategy: Lowest fan speed at modulated water flow (max auto).";
    }
  }

  // ACB strategy line.
  const ACB_STRATEGY_LABELS = {
    given_flow_given_sp: "Given PA flow at given static pressure",
    given_flow_lowest_sp: "Given PA flow for lowest static pressure",
    lowest_flow_given_sp: "Lowest PA flow at given static pressure",
    lowest_flow_lowest_sp: "Lowest PA flow and lowest static pressure",
  };
  let acbText = null;
  if (showACB) {
    const label = ACB_STRATEGY_LABELS[acbStrategy] || ACB_STRATEGY_LABELS["given_flow_given_sp"];
    acbText = `ACB strategy: ${label}.`;
  }

  const lines = [
    `Sizing method — Capacity basis: ${basisText}.`,
    !ewsOnly && facbText ? facbText : null,
    !ewsOnly && acbText ? acbText : null,
    "Reference: AHRI 1335.",
  ].filter(Boolean);

  return lines.join(" ");
}

// ---------------------------------------------------------------------------
// getRoomStaticPressureInwg
//
// Returns the effective static pressure for an ACB room, in inWG (IP).
// Preference order:
//   1. Engineer-supplied room.pa_static_pressure_inwg (positive finite number)
//   2. Engineer-supplied project-level SP (project.advanced.pa_pressure_in_wg —
//      set on the Design Conditions tab). This is the "default" that flows
//      to every room when no per-room override is set; without this fallback
//      the Summary placeholder shows the BACKEND-RESOLVED SP (the engine's
//      nozzle pick), which can drift from the value the engineer typed.
//   3. Backend-resolved nozzle SP from the calculation result
//   4. null  — caller renders em-dash or "(default)" annotation as appropriate
//
// IMPORTANT: never write the returned value back to the room object; it stays
// read-only so the engineer's blank input is not silently overwritten.
// ---------------------------------------------------------------------------
export function getRoomStaticPressureInwg(room, result, project = null) {
  const v = parseFloat(room?.pa_static_pressure_inwg);
  if (Number.isFinite(v) && v > 0) return v;

  const projSp = parseFloat(project?.advanced?.pa_pressure_in_wg);
  if (Number.isFinite(projSp) && projSp > 0) return projSp;

  const raw = result?.raw;
  if (!raw) return null;

  return (
    raw.nozzle_selection?.static_pressure_inwg ??
    raw.optimal_point?.nozzle_selection?.static_pressure_inwg ??
    raw.results?.[0]?.cooling?.speeds?.[0]?.nozzle_selection?.static_pressure_inwg ??
    null
  );
}

